import 'react-datepicker/dist/react-datepicker.css';
import './date-input.css';

import { CalendarIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'radash';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';

import { useFormattedDate } from '@/components/datamodel/hooks/useFormattedDate';
import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';
import { useTabNavigation } from '@/hooks/useTabNavigation';
import { logger } from '@/services/logger';
import { cn } from '@/services/utils';

import { IconButton } from '../IconButton';

interface DateInputProps {
    className?: string;
    borderless?: boolean;
    id?: string;
    placeholder?: string;
    onChange?: (value: Date | null) => void;
    selectedDate?: Date;
    disabled?: boolean;
    withTime?: boolean;
    focusDate?: boolean;
    gridMode?:boolean;
    /** Open the calendar as soon as the input mounts. */
    openOnMount?: boolean;
    handleUrlChange?: () => void;
}

const PopperContainer = ({ children }: PropsWithChildren) => {
    const body = document.querySelector('body') as HTMLBodyElement;

    return ReactDOM.createPortal(
        children,
        body,
    );
};

const DateInput = ({
    id,
    borderless = false,
    className,
    placeholder,
    onChange,
    selectedDate,
    withTime = false,
    disabled = false,
    gridMode =false,
    focusDate,
    openOnMount = false,
    handleUrlChange
}: DateInputProps) => {
    const TIME_FORMAT = 'HH:mm';
    const datePickerRef = useRef<DatePicker>(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const formResetContext = useFormReset();

    let isResetting = false;
    try {
        isResetting = formResetContext?.isResetting || false;
    } catch (error) {
        isResetting = false;
    }

    const { isLocaleLoaded } = useFormattedDate(); // Get locale loading state
    const handleTabNavigation = useTabNavigation();

    useEffect(() => {
        if(isResetting) return;
        if (focusDate && buttonRef.current) {
            buttonRef.current.focus();
            datePickerRef.current?.setFocus();
        }
    }, [focusDate, isResetting]);

    // Hit list: the double-click that enters edit mode should also show
    // the calendar. The input is not in the DOM for that click, so a
    // later click would otherwise be required.
    useEffect(() => {
        if (!openOnMount || disabled || isResetting || !isLocaleLoaded) return;
        datePickerRef.current?.setOpen(true);
        setIsCalendarOpen(true);
    }, [openOnMount, disabled, isResetting, isLocaleLoaded]);

    const onClick = () => {
        if (datePickerRef.current) {
            datePickerRef.current.setOpen(!isCalendarOpen);
            setIsCalendarOpen(!isCalendarOpen);
        }
    };

    // Render a loader or placeholder if locale is not loaded
    if (!isLocaleLoaded) {
        return;
    }

    const parsedSelectedDate = !isEmpty(selectedDate)?
        selectedDate instanceof Date
            ? selectedDate
            : typeof selectedDate === 'string'
                ? new Date(selectedDate)
                : typeof selectedDate === 'number'
                    ? new Date(selectedDate)
                    : undefined: undefined;

    // Prevent the editor from closing when an error occurs with the date
    if (!isEmpty(parsedSelectedDate) && parsedSelectedDate && isNaN(parsedSelectedDate.getTime())) {
        logger.error('Invalid date detected:', parsedSelectedDate);
        return;
    }

    return (
        <div className="tw-flex tw-w-full tw-relative">
            <DatePicker
                ref={datePickerRef}
                portalId="root-portal"
                shouldCloseOnSelect
                closeOnScroll= {(e) => {
                    const { target } = e;
                    if (!(target instanceof Element)) {
                        return false;
                    }
                    return !target.closest('.react-datepicker-popper');
                }}
                popperProps={{ strategy: 'fixed' }}
                className={cn(
                    `
                    scoped-date-time
                    ${borderless ? 'borderless-date-time tw-mx-1' : 'bordered-date-time'}
                    tw-flex
                    tw-h-9
                    tw-w-full
                    tw-bg-transparent
                    lg-font-dynamic
                    tw-text-t-primary
                    tw-p-[6px_12px]
                    placeholder-tw-text-t-primary
                    focus-visible:!tw-border-primary-focus
                    disabled:tw-text-t-inactive
                    `,
                    gridMode && 'grid-row-highlightable-input',
                    !gridMode && `
                        disabled:tw-border-primary
                        disabled:tw-bg-fill-secondary`,
                    className,
                )}
                id={id}
                placeholderText={placeholder}
                popperContainer={PopperContainer}
                disabled={disabled}
                onFocus={handleUrlChange}
                showTimeSelect={withTime}
                selected={parsedSelectedDate ?? null}
                timeFormat={TIME_FORMAT}
                timeIntervals={1}
                onChange={(date) => {onChange?.(date ?? null);}}
                dateFormat={withTime ? 'dd.MM.yyyy, HH:mm' : 'dd.MM.yyyy'}
                autoComplete='off'
                showMonthDropdown
                showYearDropdown
                dropdownMode="scroll"
                onKeyDown={handleTabNavigation}
            />

            {!borderless && (
                <div className={cn('input tw-px-3 tw-flex tw-items-center tw-bg-gray-100 tw-whitespace-nowrap disabled:tw-icon-inactive')}>
                    <IconButton
                        icon={CalendarIcon}
                        ref={buttonRef}
                        type="button"
                        disabled={disabled}
                        onClick={onClick}
                        size="md"
                    />
                </div>
            )}
        </div>
    );
};

export { DateInput };
